import {
    Component,
    DestroyRef,
    effect,
    inject,
    input,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DealerService } from '@core/services/dealer/dealer.service';
import { Button, Input, Modal } from '@ntv360/component-pantry';
import { AutocompleteComponent } from '@shared/components/autocomplete/autocomplete.component';
import { Logger, withLoading } from '@shared/utils';
import { catchError, EMPTY } from 'rxjs';

export interface GenerateLicenseForm {
    dealerId: string | null;
    count: number | null;
}

@Component({
    selector: 'app-generate-license-modal',
    standalone: true,
    imports: [Modal, Button, Input, FormsModule, AutocompleteComponent],
    templateUrl: './generate-license-modal.component.html',
    styleUrl: './generate-license-modal.component.scss',
})
export class GenerateLicenseModalComponent implements OnInit {
    /** Service for fetching dealer data. */
    private dealerService = inject(DealerService);

    /** Destroy ref used to scope HTTP subscriptions to the component lifecycle. */
    private destroyRef = inject(DestroyRef);

    /** Controls modal visibility. Required by the parent. */
    public isVisible = input.required<boolean>();

    /** Emitted when the modal is closed by the user. */
    public modalClose = output<void>();

    /** Emitted when the user submits the form with valid dealer and count values. */
    public generate = output<GenerateLicenseForm>();

    /** Flat list of dealer options rendered in the autocomplete dropdown. */
    public dealerOptions = signal<{ label: string; value: string }[]>([]);

    /** True while the initial page of dealers is being fetched. */
    public isLoadingDealers = signal(false);

    /** True while an additional page of dealers is being appended (infinite scroll). */
    public isLoadingMore = signal(false);

    /** True while the generate API call is in flight. Blocks close and disables the button. */
    public isGenerating = signal(false);

    /** Current pagination page for the dealer list. */
    private currentPage = signal(1);

    /** Whether the dealer API has a next page available for infinite scroll. */
    private hasNextPage = signal(false);

    /** Current search term for dealer business name filtering. */
    private searchTerm = signal('');

    /** Tracks the autocomplete's selected items for two-way binding and reset on close. */
    public selectedDealerOptions = signal<{ label: string; value: string }[]>([]);

    /** Current form state holding the selected dealer ID and license count. */
    public form = signal<GenerateLicenseForm>({ dealerId: null, count: null });

    /** Static config passed to the dealer autocomplete. */
    public autocompleteConfig = { placeholder: 'Ex. NCompass TV', searchable: true };

    constructor() {
        effect(() => {
            if (!this.isVisible()) {
                this.form.set({ dealerId: null, count: null });
                this.selectedDealerOptions.set([]);
                this.isGenerating.set(false);
                this.currentPage.set(1);
                this.hasNextPage.set(false);
                this.searchTerm.set('');
            }
        });
    }

    /** True when both a dealer and a positive license count are selected. */
    get isFormValid(): boolean {
        const { dealerId, count } = this.form();
        return !!dealerId && !!count && count > 0;
    }

    ngOnInit(): void {
        this.loadDealers();
    }

    /**
     * Fetches the first page of dealers on init and populates the autocomplete options.
     * Also seeds the pagination state (`currentPage`, `hasNextPage`).
     */
    private loadDealers(search = ''): void {
        this.dealerService
            .getAll({
                limit: 100,
                page: 1,
                search: search || undefined,
                searchFields: search ? 'clientInformation.businessName' : undefined,
            })
            .pipe(
                withLoading(this.isLoadingDealers),
                catchError((err: unknown) => {
                    Logger.error('GenerateLicenseModal', 'Failed to load dealers', err);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((res) => {
                this.dealerOptions.set(
                    res.data.map((d) => ({
                        label: d.clientInformation.businessName,
                        value: d.user.userId,
                    })),
                );
                this.hasNextPage.set(res.meta.pagination.hasNextPage);
                this.currentPage.set(1);
            });
    }

    /**
     * Triggered by the autocomplete's scroll-end event.
     * Fetches the next page of dealers and appends them to the existing options.
     * No-ops if there is no next page or a fetch is already in flight.
     */
    protected onScrollEnd(): void {
        if (!this.hasNextPage() || this.isLoadingMore()) return;
        const nextPage = this.currentPage() + 1;
        const search = this.searchTerm();
        this.dealerService
            .getAll({
                limit: 100,
                page: nextPage,
                search: search || undefined,
                searchFields: search ? 'clientInformation.businessName' : undefined,
            })
            .pipe(
                withLoading(this.isLoadingMore),
                catchError((err: unknown) => {
                    Logger.error('GenerateLicenseModal', 'Failed to load more dealers', err);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((res) => {
                const newOptions = res.data.map((d) => ({
                    label: d.clientInformation.businessName,
                    value: d.user.userId,
                }));
                this.dealerOptions.update((opts) => [...opts, ...newOptions]);
                this.hasNextPage.set(res.meta.pagination.hasNextPage);
                this.currentPage.set(nextPage);
            });
    }

    /**
     * Triggered by the autocomplete's debounced search.
     * Refetches the dealer list using the current search term.
     */
    protected onSearch(term: string): void {
        this.searchTerm.set(term);
        this.loadDealers(term);
    }

    /**
     * Updates the form's `dealerId` when the user selects a dealer from the autocomplete.
     * @param event - The selected option, or null when the selection is cleared.
     */
    protected onDealerChange(event: { label: string; value: string } | null): void {
        this.form.update((f) => ({ ...f, dealerId: event?.value ?? null }));
    }

    /**
     * Updates the form's `count` when the license number input changes.
     * @param value - Raw string value from the input; parsed to a number or null if empty.
     */
    protected onCountChange(value: string): void {
        this.form.update((f) => ({ ...f, count: value ? Number(value) : null }));
    }

    /**
     * Emits the current form to the parent and sets `isGenerating` to block further interaction.
     * The parent is responsible for closing the modal on API success.
     */
    protected onGenerate(): void {
        if (!this.isFormValid) return;
        this.isGenerating.set(true);
        this.generate.emit(this.form());
    }

    /**
     * Emits `modalClose` to the parent unless a generation is in flight,
     * preventing accidental dismissal during an API call.
     */
    protected onClose(): void {
        if (this.isGenerating()) return;
        this.modalClose.emit();
    }
}
